/**
 * AI-generated code, moderated by lzygmanski
 */
import dayjs from 'dayjs';
import {memo, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Alert, AlertTitle} from '@/components/ui/alert';
import {ChartSkeleton} from '@/components/ui/skeleton';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useGetHealthLogMeasurementsQuery,
  useListHealthLogMetricsQuery,
} from '@/services/graphqlHooks';
import {HealthLogLineChart} from '@/shared/residents/components/HealthLogLineChart';
import {
  buildHealthLogSeries,
  type HealthLogSeries,
} from '@/shared/residents/utils/healthLogUtils';
import {cn} from '@/utils/ui';

type HealthLogMeasurementChartPreviewProps = {
  residentId: string;
  metricId: string;
  className?: string;
};

function HealthLogMeasurementChartPreview({
  residentId,
  metricId,
  className,
}: HealthLogMeasurementChartPreviewProps) {
  const {t} = useTranslation();

  const from = useMemo(
    () => dayjs().subtract(2, 'weeks').startOf('day').toISOString(),
    []
  );
  const to = useMemo(() => dayjs().endOf('day').toISOString(), []);

  const {
    data: metricsData,
    isLoading: metricsLoading,
    isError: metricsError,
  } = useListHealthLogMetricsQuery(graphqlClient);

  const selectedMetric = useMemo(
    () =>
      metricsData?.listHealthLogMetrics?.find(metric => metric.id === metricId),
    [metricId, metricsData]
  );

  const {
    data: measurementsData,
    isLoading: measurementsLoading,
    isError: measurementsError,
  } = useGetHealthLogMeasurementsQuery(
    graphqlClient,
    {
      residentId,
      metricId,
      from,
      to,
    },
    {
      enabled: Boolean(residentId && metricId),
    }
  );

  const metricSeries = useMemo<HealthLogSeries[]>(
    () =>
      selectedMetric
        ? buildHealthLogSeries(
            measurementsData?.getHealthLogMeasurements?.items ?? [],
            selectedMetric
          )
        : [],
    [measurementsData, selectedMetric]
  );

  return (
    <div
      className={cn(
        'flex min-h-75 flex-col gap-4 md:min-h-100 md:flex-1',
        className
      )}
    >
      <h3 className="text-lg font-medium">{t('Last 2 weeks')}</h3>

      {(metricsError || measurementsError) && (
        <Alert variant="destructive">
          <AlertTitle>{t('Failed to load health log data')}</AlertTitle>
        </Alert>
      )}

      {!metricsError &&
        !measurementsError &&
        (metricsLoading || measurementsLoading) && (
          <ChartSkeleton loadingLabel={t('Loading health log data...')} />
        )}

      {!metricsError &&
        !measurementsError &&
        !metricsLoading &&
        !measurementsLoading &&
        selectedMetric && <HealthLogLineChart metricSeries={metricSeries} />}
    </div>
  );
}

export default memo(HealthLogMeasurementChartPreview);
